import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  PaginationItem,
  Divider,
  SelectChangeEvent,
} from "@mui/material";
import {
  Add,
  Delete,
  Download,
  FilterList,
  MoreVert,
  Search,
  FirstPage,
  LastPage,
  Remove,
  CloudUpload,
  Close,
  PhotoCamera,
  GridOn,
} from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MapPinLocation from "../../../shared/components/MapPinLocation.tsx";
import { TrashOutline } from "react-ionicons";
import "../../../shared/components/Item.css";
import { styled } from "@mui/system";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { AxiosResponse } from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import FormHelperText from "@mui/material/FormHelperText";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  createInventory,
  inventorySearchParams,
  fetchItemPhotoBlob,
  deleteInventory,
  deleteItemPhoto,
  updateInventory,
  uploadItemPhoto,
  sesarchInventories,
  getInventories,
  createInventoryDetail,
  deleteInventoryDetail,
  updateInventoryDetail,
  getInventoryDetails,
  InventoryDetailCreatePayload,
  uploadItemFile,
  deleteItemFile,
  fetchItemFileBlob,
} from "../services/inventoryService.tsx";
import { getItemDetails } from "../../data_management/services/itemDetailsService.tsx";
import { getOfficeProjects } from "../services/projectDetailServices.tsx";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { autoTable } from "jspdf-autotable";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import LoadingOverlay from "../../../shared/components/LoadingOverlay.tsx";

interface Inventory {
  id: string;
  inventoryId: string;
  officeId: string;
  officeName?: string;
  totalItems: number;
  itemDetailId?: string;
  itemName?: string;
  numbersOfItems?: number;
  locationDetail?: string;
  conditionStatus?: string;
  itemDescription?: string;
  area?: string;
  building?: string;
  floor?: string;
  brand?: string;
  serialNumber?: string;
  assessmentDate: Date | null;
  installationDate: Date | null;
  warrantyExpiry?: Date | null;
  photo?: string;
}

interface InventoryApiResponse {
  data: Inventory[];
  total: number;
  pagination: {
    totalPages: number;
  };
}

interface InventoryDetailApiResponse {
  data: InventoryDetailsData[];
  total: number;
  pagination: {
    totalPages: number;
  };
}

interface InventoryItem {
  id: string;
  itemDetailId: string;
  itemName?: string; // optional
  numbersOfItems: number;
  locationDetail?: string; // optional
  conditionStatus?: string; // optional
  itemDescription?: string; // optional
  area?: string;
  building?: string;
  floor?: string;
  brand?: string;
  serialNumber?: string;
  assessmentDate: Date | null;
  installationDate: Date | null;
  warrantyExpiry: Date | null;
  photo?: string; // optional
  photoFile?: File | null; // optional
  documentFile?: File | null;
  documentUrl?: string;
  documentName?: string;
}

interface ItemDetail {
  id: string;
  itemDetailName: string;
}

interface AvailableItemDetails {
  id: string;
  itemDetailName: string;
  isSelected: boolean;
}

interface InventoryData {
  id: string;
  itemDetailId: string;
  itemName: string;
  numbersOfItems: number;
  locationDetail: string;
  conditionStatus: string;
  itemDescription: string;
  area?: string,
  building?: string;
  floor?: string;
  brand?: string;
  serialNumber?: string;
  assessmentDate: Date | null;
  installationDate: Date | null;
  warrantyExpiry: Date | null;
  photo?: string;
}

interface InventoryDetailsData {
  id: string;
  inventoryId: string;
  itemDetail: {
    id: string;
    itemDetailName: string;
  };
  numbersOfItems: number;
  locationDetail: string;
  conditionStatus: string;
  itemDescription: string;
  warrantyExpiry: Date | null;
  photoFile?: File | null;
  photo?: string;
}

interface ApiResponse {
  data: InventoryData[] | InventoryDetailsData[];
  total: number;
  pagination: {
    totalPages: number;
  };
}

interface AdvancedSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearchResults: (results: Inventory[]) => void;
}

const InventoryListPage = () => {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState<Inventory[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddInventoryDialog, setOpenAddInventoryDialog] = useState(false);
  const [openEditInventoryDialog, setOpenEditInventoryDialog] = useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemDetails, setItemDetails] = useState<ItemDetail[]>([]);
  const [allInventoryDetails, setAllInventoryDetails] = useState<
    Record<string, any[]>
  >({});
  const [availableItemDetails, setAvailableItemDetails] = useState<
    AvailableItemDetails[]
  >([]);

  const [startDateE, setStartDateE] = useState<string | null>(null);
  const [endDateE, setEndDateE] = useState<string | null>(null);

  const emptyInventoryData: InventoryData = {
    id: "",
    itemDetailId: "",
    itemName: "",
    numbersOfItems: 0,
    locationDetail: "",
    conditionStatus: "",
    itemDescription: "",
    area: "",
    building: "",
    floor: "",
    brand: "",
    serialNumber: "",
    assessmentDate: null,
    installationDate: null,
    warrantyExpiry: null,
    photo: "",
  };

  const [inventoryDetails, setInventoryDetails] = useState<InventoryItem[]>([]);
  const [mainInventoryId, setMainInventoryId] = useState<string>("");
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [inventoryData, setInventoryData] =
    useState<InventoryData>(emptyInventoryData);

  const resetForm = () => {
    setInventoryDataEdit({
      ...inventoryDataEdit,
      itemDetailId: "",
      itemName: "",
      numbersOfItems: 0,
      locationDetail: "",
      conditionStatus: "",
      itemDescription: "",
      area: "",
      building: "",
      floor: "",
      brand: "",
      serialNumber: "",
      assessmentDate: null,
      installationDate: null,
      warrantyExpiry: null,
    });
    setEditPhotoPreview("");
    setSelectedTableRow(null);
    setIsEditingExistingItem(false);
  };

  const [selectedItemAdd, setSelectedItemAdd] = useState<ItemDetail | null>(
    null
  );
  const [selectedItemEdit, setSelectedItemEdit] = useState<ItemDetail | null>(
    null
  );
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  const location = useLocation();

  const [officeOptions, setOfficeOptions] = useState<
    Array<{
      officeId: string;
      officeName: string;
    }>
  >([]);
  const [selectedOffice, setSelectedOffice] = useState<{
    officeId: string;
    officeName: string;
  } | null>(null);

  const offset = (currentPage - 1) * rowsPerPage;

  const [filterParams, setFilterParams] = useState<inventorySearchParams>({});

  // Sort
  const [sortConfig, setSortConfig] = useState<{
    orderBy: string;
    order: "asc" | "desc";
  }>({
    orderBy: "createdAt",
    order: "desc",
  });

  const fetchOfficeProjectList = async () => {
    try {
      const result = await getOfficeProjects();
      setOfficeOptions(
        result.data.map((project: any) => ({
          officeId: project.office.officeId,
          officeName: project.office.officeName,
        }))
      );
    } catch (error) {
      console.error("Failed to fetch offices:", error);
    }
  };

  const reloadInventory = async () => {
    setIsLoading(true);
    setError("");
    try {
      // Load inventory headers
      const inventoryResult = await getInventories({
        ...filterParams,
        limit: rowsPerPage,
        offset,
        orderBy: sortConfig.orderBy,
        order: sortConfig.order,
      });

      // Transform data langsung dari response
      const transformedData = inventoryResult.data.map((item: any) => ({
        id: item.id,
        officeId: item.office?.officeId || "",
        officeName: item.office?.officeName || "",
        totalItems: item.totalItems || 0,
      }));

      setRows(transformedData);
      setTotalItems(inventoryResult.total || 0);
      setTotalPages(inventoryResult.pagination?.totalPages || 1);
    } catch (err: any) {
      setError(err.message || "Failed to load inventory");
      console.error("Error loading inventory:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column: string) => {
    setSortConfig((prev) => {
      if (prev.orderBy === column) {
        return {
          orderBy: column,
          order: prev.order === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          orderBy: column,
          order: "asc",
        };
      }
    });
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchItemDetails();
    reloadInventory();
    fetchOfficeProjectList();
  }, [currentPage, rowsPerPage, filterParams, sortConfig]);

  // useEffect(() => {
  //   const fetchAllInventoryDetails = async () => {
  //     try {
  //       // Gunakan data rows yang sudah di-load oleh reloadInventory
  //       if (rows.length === 0) return; // Skip jika belum ada data

  //       // Fetch details hanya untuk rows yang sudah ada
  //       const detailsPromises = rows.map((row) =>
  //         getInventoryDetails({ inventoryId: row.id, limit: 100, offset: 0 })
  //       );

  //       const detailsResponses = await Promise.all(detailsPromises);

  //       const detailsMap = {};
  //       rows.forEach((row, index) => {
  //         detailsMap[row.id] = detailsResponses[index].data || [];
  //       });

  //       setAllInventoryDetails(detailsMap);
  //     } catch (error) {
  //       console.error("Failed to pre-fetch inventory details:", error);
  //     }
  //   };

  //   fetchAllInventoryDetails();
  // }, [rows]); // Trigger ketika rows berubah

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const CustomTextField = styled(TextField)({
    "& .MuiInputBase-input": {
      backgroundColor: "#f5f5f5",
      borderRadius: 4,
      padding: "8px 12px",
    },
  });

  const handleDeleteInventory = async () => {
    const confirmDelete = await Swal.fire({
      title: "Apakah Anda yakin?",
      text: "Ini akan menghapus inventory beserta semua item dan foto yang terkait!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
    });

    if (!confirmDelete.isConfirmed) return;

    try {
      setIsLoading(true);
      const rowsToDelete = [...selectedRows];

      // Ekstrak nama inventory untuk pesan sukses
      const deletedNames = rows
        .filter((r) => rowsToDelete.includes(r.id))
        .map((r) => r.officeName)
        .filter(Boolean);

      // Proses penghapusan
      await Promise.all(
        rowsToDelete.map(async (inventoryId) => {
          const detailsResponse = await getInventoryDetails({
            inventoryId,
            limit: 1000,
            offset: 0,
          });

          const inventoryDetails = detailsResponse.data || [];

          await Promise.all(
            inventoryDetails.map(async (detail) => {
              try {
                if (detail.photo) {
                  await deleteItemPhoto(detail.id, "inventory");
                }
                await deleteInventoryDetail(detail.id);
              } catch (error) {
                console.error(`Gagal menghapus detail:`, error);
              }
            })
          );

          await deleteInventory(inventoryId);
        })
      );

      // Pastikan loading dimatikan sebelum show success
      setIsLoading(false);

      await Swal.fire({
        icon: "success",
        title: "Berhasil Dihapus",
        text:
          deletedNames.length > 0
            ? `Inventory ${deletedNames.join(", ")} telah dihapus`
            : `${rowsToDelete.length} inventory telah dihapus`,
        confirmButtonColor: "#0F62FE",
      });

      setSelectedRows([]);
      reloadInventory();
      setOpenDeleteDialog(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Gagal menghapus:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal Menghapus",
        text:
          error.response?.data?.message || "Terjadi kesalahan saat menghapus",
      });
    }
  };

  useEffect(() => {
    setSelectedRows([]);
  }, [rows]);

  const [filters, setFilters] = useState([{ field: "itemName", value: "" }]);

  const availableFilters = [
    { value: "officeId", label: "Office" },
    { value: "totalItems", label: "Total Items" },
    // { value: "itemName", label: "Item Name" },
    // { value: "numbersOfItems", label: "Numbers Of Items" },
    // { value: "locationDetail", label: "Location Detail" },
    // { value: "conditionStatus", label: "Condition Status" },
    // { value: "itemDescription", label: "Item Description" },
    // { value: "warrantyExpiry", label: "Warranty/Expired Date" },
  ];

  const handleFilterChange = (index: number, key: string, value: string) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;
    setFilters(newFilters);
  };

  const handleAddFilter = () => {
    setFilters([...filters, { field: "", value: "" }]);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleApplyFilters = async () => {
    setIsLoading(true);
    try {
      const searchParams: inventorySearchParams = {};

      filters.forEach((filter) => {
        if (filter.value.trim() !== "") {
          switch (filter.field) {
            case "officeId":
              const office = officeOptions.find(
                (o) => o.officeName === filter.value
              );
              searchParams.officeId = office?.officeId || filter.value;
              break;
            default:
              searchParams[filter.field] = filter.value;
          }
        }
      });

      setFilterParams(searchParams);
      setOpenFilterDialog(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to fetch data",
        text: error.response?.data?.message || "An error occurred",
      });
      handleCloseFilter();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseFilter = () => {
    setOpenFilterDialog(false);
  };

  const handleResetFilters = () => {
    setFilters([{ field: "itemName", value: "" }]);
    setFilterParams({});
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setError("");
    try {
      setFilterParams({
        keyword: searchQuery,
      });
    } catch (err) {
      setError(err.message || "Search failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      Swal.fire({
        title: "Preparing Export...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const exportData = await getExportData();
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Inventory");
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Inventory_${date}.xlsx`);
      Swal.close();
    } catch (error) {
      console.error("Export failed:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: error.message || "Failed to export data",
      });
    }
  };

  const getExportData = async () => {
    const allInventory = await getInventories({
      ...filterParams,
      limit: 1000000,
      offset: 0,
      orderBy: sortConfig.orderBy,
      order: sortConfig.order,
    });

    return allInventory.data.map((item) => ({
      Office: item.office?.officeName || "-",
      "Total Items": item.totalItems || "-",
      // "Item Name": item.itemName || "-",
      // "Numbers Of Items": item.numbersOfItems || "-",
      // "Location Detail": item.locationDetail || "-",
      // "Condition Status": item.conditionStatus || "-",
      // "Item Description": item.itemDescription || "-",
      // "Warranty Expiry": item.warrantyExpiry
      //   ? new Date(item.warrantyExpiry).toLocaleDateString()
      //   : "-",
      "Created At": item.createdAt
        ? new Date(item.createdAt).toLocaleString()
        : "-",
      "Updated At": item.updatedAt
        ? new Date(item.updatedAt).toLocaleString()
        : "-",
    }));
  };

  const exportInventoryItemsToPDF = async () => {
    try {
      const doc = new jsPDF("landscape"); // Gunakan landscape untuk lebih banyak space

      // 1. Add header image
      try {
        const headerImageUrl = window.location.origin + "/img/KOP_SPP.jpg";
        const response = await fetch(headerImageUrl);
        if (response.ok) {
          const blob = await response.blob();
          const base64Image = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          doc.addImage(base64Image, "JPEG", -10, 0, 230, 40);
        }
      } catch (imgError) {
        console.error("Failed to load header image:", imgError);
      }

      // Add horizontal line after header
      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(10, 37, 280, 37);

      // 2. Main content
      const startY = 60;

      // Main title
      doc.setFontSize(16);
      const titleText = `Office: ${inventoryDataEdit.officeName || "-"}`;
      doc.text(titleText, 145, startY - 15, { align: "center" });

      // Total Items
      doc.setFontSize(10);
      doc.text(
        `Total Item : ${inventoryDataEdit.totalItems || "-"}`,
        15,
        startY
      );

      // ===== INVENTORY ITEMS TABLE =====
      const inventoryItemsTitleY = startY + 10;
      doc.setFontSize(12);
      doc.text("Inventory Items", 15, inventoryItemsTitleY);

      // Konfigurasi kolom untuk fit to screen
      const columnStyles = {
        0: { cellWidth: 15 }, // No
        1: { cellWidth: 30 }, // Item Name
        2: { cellWidth: 25 }, // Location
        3: { cellWidth: 10 }, // Quantity
        4: { cellWidth: 30 }, // Description
        5: { cellWidth: 25 }, // Condition
        6: { cellWidth: 20 }, // Building
        7: { cellWidth: 10 }, // Floor
        8: { cellWidth: 20 }, // Brand
        9: { cellWidth: 20 }, // Serial Number
        10: { cellWidth: 20 }, // Assessment Date
        11: { cellWidth: 20 }, // Installation Date
        12: { cellWidth: 20 }, // Warranty Expiry
      };

      autoTable(doc, {
        head: [
          [
            "No",
            "Item Name",
            "Location",
            "Qty",
            "Description",
            "Condition",
            "Building",
            "Floor",
            "Brand",
            "Serial Number",
            "Assessment Date",
            "Installation Date",
            "Warranty Expiry",
          ],
        ],
        body: editedItems.filter((row) => {
          if (!startDateE && !endDateE) return true; // tidak ada filter

          const warrantyExpiry = dayjs(row.warrantyExpiry);
          const start = startDateE ? dayjs(startDateE) : null;
          const end = endDateE ? dayjs(endDateE) : null;

          if (start && end) {
            return warrantyExpiry.isSameOrAfter(start) && warrantyExpiry.isSameOrBefore(end);
          }
          if (start) {
            return warrantyExpiry.isSameOrAfter(start);
          }
          if (end) {
            return warrantyExpiry.isSameOrBefore(end);
          }
          return true;
        }).map((item, i) => [
          `${i + 1}`,
          item.itemName || "-",
          item.locationDetail || "-",
          item.numbersOfItems?.toString() || "-",
          item.itemDescription || "-",
          item.conditionStatus || "-",
          item.building || "-",
          item.floor || "-",
          item.brand || "-",
          item.serialNumber || "-",
          item.assessmentDate
            ? new Date(item.assessmentDate).toLocaleDateString("id-ID")
            : "-",
          item.installationDate
            ? new Date(item.installationDate).toLocaleDateString("id-ID")
            : "-",
          item.warrantyExpiry
            ? new Date(item.warrantyExpiry).toLocaleDateString("id-ID")
            : "-",
        ]),
        startY: inventoryItemsTitleY + 8,
        margin: { left: 15, right: 5 },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 8,
        },
        styles: {
          fontSize: 6,
          cellPadding: 1,
          overflow: "linebreak",
          halign: "center",
        },
        columnStyles: columnStyles,
        theme: "grid",
        tableWidth: "auto",
        didDrawPage: function (data) {
          // Footer pada setiap halaman
          doc.setFontSize(8);
          doc.setTextColor(100);
          doc.text(
            `Page ${data.pageNumber}`,
            280,
            doc.internal.pageSize.height - 10,
            { align: "right" }
          );
        },
      });

      // Footer utama
      const finalY = (doc as any).lastAutoTable.finalY + 15;
      doc.setFontSize(8);
      doc.setTextColor(100);
      // doc.text("Generated by Inventory System", 145, finalY, { align: "center" });
      // doc.text(new Date().toLocaleString('id-ID'), 145, finalY + 5, { align: "center" });

      // 3. Save PDF
      const date = new Date().toISOString().slice(0, 10);
      const fileName = `Inventory_${inventoryDataEdit.officeName?.replace(/\s+/g, "_") || "unknown"
        }_${date}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Failed to generate PDF document",
      });
    }
  };

  const exportInventoryItemsToExcel = async () => {
    try {
      // Prepare data for Excel export
      // const project = projectOptions.find(pro => pro.id === editRabData.buildingLocationId) || {};
      // console.log(project);

      // 1. Create workbook
      const wb = XLSX.utils.book_new();

      // 2. Create Project Information sheet
      const projectInfoData = [
        ["Office Name", inventoryDataEdit.officeName || "-"],
        ["Total Items", inventoryDataEdit.totalItems || "-"],
      ];

      const projectInfoWs = XLSX.utils.aoa_to_sheet([
        ["Inventory"],
        [], // empty row
        ...projectInfoData,
      ]);

      // 3. Create Inventory Items sheet
      const inventoryItemsHeader = [
        "No",
        "Item Name",
        "Location",
        "Quantity",
        "Description",
        "Condition",
        "Building",
        "Floor",
        "Brand",
        "Serial Number",
        "Assessment Date",
        "Installation Date",
        "Warranty Expiry",
      ];

      const inventoryItemsData = editedItems.filter((row) => {
        if (!startDateE && !endDateE) return true; // tidak ada filter

        const warrantyExpiry = dayjs(row.warrantyExpiry);
        const start = startDateE ? dayjs(startDateE) : null;
        const end = endDateE ? dayjs(endDateE) : null;

        if (start && end) {
          return warrantyExpiry.isSameOrAfter(start) && warrantyExpiry.isSameOrBefore(end);
        }
        if (start) {
          return warrantyExpiry.isSameOrAfter(start);
        }
        if (end) {
          return warrantyExpiry.isSameOrBefore(end);
        }
        return true;
      }).map((item, index) => [
        index + 1,
        item.itemName || "-",
        item.locationDetail || "-",
        item.numbersOfItems || "-",
        item.itemDescription || "-",
        item.conditionStatus || "-",
        item.building || "-",
        item.floor || "-",
        item.brand || "-",
        item.serialNumber || "-",
        item.assessmentDate
          ? new Date(item.assessmentDate).toLocaleDateString("id-ID")
          : "-",
        item.installationDate
          ? new Date(item.installationDate).toLocaleDateString("id-ID")
          : "-",
        item.warrantyExpiry
          ? new Date(item.warrantyExpiry).toLocaleDateString("id-ID")
          : "-",
      ]);

      const inventoryItemsWs = XLSX.utils.aoa_to_sheet([
        ["Inventory"],
        [], // empty row
        ...projectInfoData,
        [],
        ["Inventory Items"],
        [],
        inventoryItemsHeader,
        ...inventoryItemsData,
        [],
      ]);

      // Lebar kolom
      inventoryItemsWs["!cols"] = [
        { wch: 5 }, // No
        { wch: 25 }, // Category
        { wch: 35 }, // Description
        { wch: 10 }, // Volume
        { wch: 10 }, // Unit
        { wch: 25 }, // Label footer
        { wch: 20 }, // Nominal
      ];

      // Merge title cells
      projectInfoWs["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }, // Merge PROJECT INFORMATION title
      ];

      inventoryItemsWs["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }, // Merge INVENTORY ITEMS title
        {
          s: { r: inventoryItemsData.length + 3, c: 0 },
          e: { r: inventoryItemsData.length + 3, c: 6 },
        }, // Merge amount in words
        {
          s: { r: inventoryItemsData.length + 4, c: 0 },
          e: { r: inventoryItemsData.length + 4, c: 6 },
        }, // Merge amount in words value
      ];

      // 5. Add sheets to workbook
      // XLSX.utils.book_append_sheet(wb, projectInfoWs, "Project Info");
      XLSX.utils.book_append_sheet(wb, inventoryItemsWs, "Inventory Items");

      // 6. Generate and download Excel file
      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(
        wb,
        `InventoryItems_${inventoryDataEdit.officeName || "export"
        }_${date}.xlsx`
      );
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire({
        icon: "error",
        title: "Export Failed",
        text: "Failed to generate Excel file",
      });
    }
  };

  const fetchItemDetails = async () => {
    try {
      const result = await getItemDetails({});
      setAvailableItemDetails(
        result.data.map((item: any) => ({
          id: item.id,
          itemDetailName: item.itemDetailName,
          isSelected: false, // Initialize all as not selected
        }))
      );
    } catch (error) {
      console.error("Failed to fetch item details:", error);
    }
  };

  const getAvailableItems = () => {
    return availableItemDetails.filter((item) => !item.isSelected);
  };

  // Add Inventory
  const handleOpenAddDialog = () => {
    resetAddForm();
    setOpenAddInventoryDialog(true);
  };

  const [inventoryDataAdd, setInventoryDataAdd] = useState({
    officeId: "",
    totalItems: 0,
    itemDetailId: "",
    itemName: "",
    numbersOfItems: 0,
    locationDetail: "",
    conditionStatus: "",
    itemDescription: "",
    area: "",
    building: "",
    floor: "",
    brand: "",
    serialNumber: "",
    assessmentDate: null as Date | null,
    installationDate: null as Date | null,
    warrantyExpiry: null as Date | null,
  });

  const newItemAdd = () => {
    setInventoryItems((prev) => [
      ...prev,
      {
        id: "",
        itemDetailId: "",
        itemName: "",
        numbersOfItems: 0,
        locationDetail: "",
        conditionStatus: "",
        itemDescription: "",
        area: "",
        building: "",
        floor: "",
        brand: "",
        serialNumber: "",
        assessmentDate: null,
        installationDate: null,
        warrantyExpiry: null,
        photoFile: null,
      },
    ]);
  };

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>("");

  const resetAddForm = () => {
    setInventoryDataAdd({
      officeId: "",
      totalItems: 0,
      itemDetailId: "",
      itemName: "",
      numbersOfItems: 0,
      locationDetail: "",
      conditionStatus: "",
      itemDescription: "",
      area: "",
      building: "",
      floor: "",
      brand: "",
      serialNumber: "",
      assessmentDate: null,
      installationDate: null,
      warrantyExpiry: null,
    });
    setInventoryItems([]);
    setAvailableItemDetails((prev) =>
      prev.map((item) => ({ ...item, isSelected: false }))
    );
    setSelectedOffice(null);
  };

  const handleItemChangeAdd = (
    index: number,
    field: keyof InventoryItem,
    value: any
  ) => {
    setInventoryItems((prev) => {
      const newItems = [...prev];

      // If changing itemDetailId, update the available items
      if (field === "itemDetailId") {
        const previtemDetailId = newItems[index].itemDetailId;
        const newitemDetailId = value;

        // Mark previous item as not selected (if it existed)
        if (previtemDetailId) {
          setAvailableItemDetails((prevDetails) =>
            prevDetails.map((item) =>
              item.id === previtemDetailId
                ? { ...item, isSelected: false }
                : item
            )
          );
        }

        // Mark new item as selected
        if (newitemDetailId) {
          setAvailableItemDetails((prevDetails) =>
            prevDetails.map((item) =>
              item.id === newitemDetailId ? { ...item, isSelected: true } : item
            )
          );
        }

        // Find the item name
        const selectedItem = availableItemDetails.find(
          (item) => item.id === newitemDetailId
        );
        newItems[index] = {
          ...newItems[index],
          itemDetailId: newitemDetailId,
          itemName: selectedItem?.itemDetailName || "",
        };
      } else {
        newItems[index] = {
          ...newItems[index],
          [field]: value,
        };
      }

      return newItems;
    });
  };

  const handleItemFileUploadAdd = (index: number, file: File) => {
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire(
        "Error",
        "File format not supported. Allowed: PDF, Word, Excel",
        "error"
      );
      return;
    }

    if (file.size > maxSize) {
      Swal.fire("Warning", "Maximum file size is 10MB", "warning");
      return;
    }

    const newPreviewUrl = URL.createObjectURL(file);
    handleItemChangeAdd(index, "photo", newPreviewUrl);
    handleItemChangeAdd(index, "photoFile", file);
  };

  const handleInputChangeAdd = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setInventoryDataAdd({
      ...inventoryDataAdd,
      [name]: value,
    });
  };

  const handleNumberInputChangeAdd = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setInventoryDataAdd({
      ...inventoryDataAdd,
      [name]: Number(value),
    });
  };

  const handleDateChangeAdd = (name: string, value: Date | null) => {
    setInventoryDataAdd({
      ...inventoryDataAdd,
      [name]: value,
    });
  };

  const [errorsAdd, setErrorsAdd] = useState({
    officeId: false,
    totalItems: false,
    itemName: false,
    numbersOfItems: false,
    locationDetail: false,
    conditionStatus: false,
    itemDescription: false,
    warrantyExpiry: false,
  });

  const validateFormAdd = () => {
    const newErrors = {
      officeId: !inventoryDataAdd.officeId,
      totalItems: !inventoryDataAdd.totalItems,
      itemName: !inventoryDataAdd.itemName,
      numbersOfItems: !inventoryDataAdd.numbersOfItems,
      locationDetail: !inventoryDataAdd.locationDetail,
      conditionStatus: !inventoryDataAdd.conditionStatus,
      itemDescription: !inventoryDataAdd.itemDescription,
      warrantyExpiry: !inventoryDataAdd.warrantyExpiry,
    };
    setErrorsAdd(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const showPopupMandatoryError = () => {
    Swal.fire({
      icon: "error",
      title: "Incomplete Data",
      text: "Please fill all mandatory fields.",
    });
  };

  const handleFileUpload = (file: File) => {
    const validTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Invalid file format",
        text: "Only PNG, JPG, and JPEG files are allowed!",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "File too large",
        text: "Maximum size is 5MB",
      });
      return;
    }

    const newPreviewUrl = URL.createObjectURL(file);
    setPhotoFile(file);
    setPhotoPreview(newPreviewUrl);
  };

  const photoInputRef = useRef<HTMLInputElement>(null);

  const handleCloseAdd = () => {
    setOpenAddInventoryDialog(false);
    resetAddForm();
  };

  const handleSaveAdd = async () => {
    try {
      // Validasi dasar
      if (
        !inventoryDataAdd.officeId ||
        !inventoryDataAdd.totalItems ||
        inventoryItems.length === 0
      ) {
        showPopupMandatoryError();
        return;
      }

      // Validasi setiap item
      for (const item of inventoryItems) {
        if (
          !item.itemDetailId ||
          !item.numbersOfItems ||
          !item.locationDetail ||
          !item.conditionStatus ||
          !item.itemDescription ||
          !item.area ||
          !item.building ||
          !item.floor ||
          !item.brand ||
          !item.serialNumber ||
          !item.assessmentDate ||
          !item.installationDate ||
          !item.warrantyExpiry
        ) {
          Swal.fire({
            icon: "error",
            title: "Incomplete Data",
            text: "Please fill all mandatory fields for all items.",
          });
          return;
        }
      }

      // Step 1: Create Inventory (Header)
      const inventoryResponse = await createInventory({
        officeId: inventoryDataAdd.officeId,
        totalItems: inventoryDataAdd.totalItems,
        quantity: 0,
        itemName: " ", // Diisi kosong karena detail ada di item
        conditionStatus: " ", // Diisi kosong karena detail ada di item
      });

      // Step 2: Create Inventory Details untuk setiap item
      await Promise.all(
        inventoryItems.map(async (item) => {
          const detailPayload = {
            inventoryId: inventoryResponse.id, // ID dari response createInventory
            itemDetailId: item.itemDetailId,
            numberOfItems: item.numbersOfItems,
            locationInfo: item.locationDetail,
            assetCondition: item.conditionStatus,
            detailDescription: item.itemDescription,
            area: item.area,
            building: item.building,
            floor: item.floor,
            brand: item.brand,
            serialNumber: item.serialNumber,
            assessmentDate: item.assessmentDate,
            installationDate: item.installationDate,
            warrantyExpiry: item.warrantyExpiry,
          };

          const detailResponse = await createInventoryDetail(detailPayload);

          // Step 3: Upload foto jika ada
          if (item.photoFile) {
            await uploadItemPhoto(detailResponse.id, item.photoFile);
          }
        })
      );

      // Reset form dan tampilkan notifikasi sukses
      setOpenAddInventoryDialog(false);
      resetAddForm();

      Swal.fire({
        icon: "success",
        title: "Inventory created successfully!",
        confirmButtonColor: "#0F62FE",
      });

      // Refresh data
      reloadInventory();
    } catch (error: any) {
      console.error("Error creating inventory:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to create inventory",
        text: error.response?.data?.message || error.message,
      });
    }
  };

  // Edit Inventory
  const [isEditing, setIsEditing] = useState(false);
  const [selectedInventoryId, setSelectedInventoryId] = useState<string | null>(
    null
  );
  const [inventoryDataEdit, setInventoryDataEdit] = useState<Inventory>({
    id: "",
    inventoryId: "",
    officeId: "",
    officeName: "",
    totalItems: 0,
    itemDetailId: "",
    itemName: "",
    numbersOfItems: 0,
    locationDetail: "",
    conditionStatus: "",
    itemDescription: "",
    area: "",
    building: "",
    floor: "",
    brand: "",
    serialNumber: "",
    assessmentDate: null,
    installationDate: null,
    warrantyExpiry: null,
  });

  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string>("");

  const [selectedTableRow, setSelectedTableRow] =
    useState<InventoryItem | null>(null);
  const [originalItems, setOriginalItems] = useState<InventoryItem[]>([]);
  const [editedItems, setEditedItems] = useState<InventoryItem[]>([]);
  const [baseUrl, setBaseUrl] = useState(""); // Your API base URL
  const [isEditingExistingItem, setIsEditingExistingItem] = useState(false);

  // In the handleOpenEditDialog function, update the photo handling:
  const handleOpenEditDialog = async (inventory: Inventory) => {
    try {
      setIsLoading(true);
      setIsEditingExistingItem(false);

      // Set the inventory header data including the ID
      setInventoryDataEdit({
        id: inventory.id,
        inventoryId: inventory.id,
        officeId: inventory.officeId,
        officeName: inventory.officeName || "",
        totalItems: inventory.totalItems,
        itemDetailId: "",
        itemName: "",
        numbersOfItems: 0,
        locationDetail: "",
        conditionStatus: "",
        area: "",
        building: "",
        floor: "",
        brand: "",
        serialNumber: "",
        assessmentDate: null,
        installationDate: null,
        itemDescription: "",
        warrantyExpiry: null,
      });
      setEditPhotoPreview("");
      setSelectedTableRow(null);

      // Load inventory details
      const detailsResponse = await getInventoryDetails({
        inventoryId: inventory.id,
        limit: 100,
        offset: 0,
      });

      const inventoryDetails = detailsResponse.data || [];

      // Transform details to include file URLs
      const transformedDetails = await Promise.all(
        inventoryDetails.map(async (detail) => {
          let fileUrl;
          let fileName;

          // Load file if exists (bisa berupa foto atau dokumen)
          if (detail.photo) {
            try {
              // Coba ambil sebagai file document
              fileUrl = await fetchItemFileBlob(detail.photo);
              fileName = detail.photo; // Nama file dari response
            } catch (error) {
              console.error("Error loading file:", error);
              fileUrl = "";
              fileName = "";
            }
          }

          return {
            id: detail.id,
            inventoryId: detail.inventoryId,
            itemDetailId: detail.itemDetail?.id || "",
            itemName: detail.itemDetail?.itemDetailName || "",
            numbersOfItems: detail.numberOfItems || 0,
            locationDetail: detail.locationInfo || "",
            conditionStatus: detail.assetCondition || "",
            itemDescription: detail.detailDescription || "",
            area: detail.area || "",
            building: detail.building || "",
            floor: detail.floor || "",
            brand: detail.brand || "",
            serialNumber: detail.serialNumber || "",
            assessmentDate: detail.assessmentDate || "",
            installationDate: detail.installationDate || "",
            warrantyExpiry: detail.warrantyExpiry
              ? new Date(detail.warrantyExpiry)
              : null,
            photo: fileUrl, // Sekarang photo bisa berisi URL ke file apapun
            photoFile: null,
            documentUrl: fileUrl, // Simpan juga di documentUrl untuk konsistensi
            documentName: fileName,
          };
        })
      );

      setEditedItems(transformedDetails);
      setOriginalItems(transformedDetails);
      setOpenEditInventoryDialog(true);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to open inventory dialog:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to open inventory",
        text: error.message || "Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update the handleTableRowClick function
  const handleTableRowClick = (item: InventoryItem) => {
    // Clean up previous photo preview if exists
    if (editPhotoPreview.startsWith("blob:")) {
      setEditPhotoPreview("");
    }

    setSelectedTableRow(item);
    setIsEditingExistingItem(true);

    // Update form data
    setInventoryDataEdit({
      ...inventoryDataEdit,
      id: item.id,
      itemDetailId: item.itemDetailId,
      itemName: item.itemName,
      numbersOfItems: item.numbersOfItems,
      locationDetail: item.locationDetail,
      conditionStatus: item.conditionStatus,
      itemDescription: item.itemDescription,
      area: item.area,
      building: item.building,
      floor: item.floor,
      brand: item.brand,
      serialNumber: item.serialNumber,
      assessmentDate: item.assessmentDate,
      installationDate: item.installationDate,
      warrantyExpiry: item.warrantyExpiry,
    });

    // Set photo preview
    setEditPhotoPreview(item.photo || "");
    setEditPhotoFile(item.photoFile || null);
  };

  const handleDownloadFile = async (filename: string) => {
    try {
      const fileData = await fetchItemFileBlob(filename);
      if (fileData) {
        window.open(fileData, "_blank");
      }
    } catch (error) {
      console.error("Gagal download file:", error);
      Swal.fire("Error", "Gagal memuat file", "error");
    }
  };

  const handleDetailChange = (field: keyof InventoryItem, value: any) => {
    if (selectedTableRow === null) return;

    setInventoryDataEdit((prev) => ({
      ...prev,
      [field]: value,
    }));

    setEditedItems((prev) => {
      const newItems = [...prev];
      newItems[selectedTableRow] = {
        ...newItems[selectedTableRow],
        [field]: value,
      };
      return newItems;
    });
  };

  const handleDeleteInventoryDetailRow = async (index: number) => {
    try {
      // Get the inventory detail from the state using the index
      const inventoryToDelete = editedItems[index];

      // Validate the data exists and has an ID
      if (!inventoryToDelete || !inventoryToDelete.id) {
        throw new Error("Data inventory tidak valid atau tidak memiliki ID");
      }

      // Confirmation dialog
      const confirmDelete = await Swal.fire({
        title: "Apakah Anda yakin?",
        text: `Anda akan menghapus inventory detail ${inventoryToDelete.itemName}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Ya, hapus!",
        cancelButtonText: "Batal",
      });

      if (!confirmDelete.isConfirmed) return;

      // Delete photo first if exists
      if (inventoryToDelete.photo) {
        try {
          await deleteItemPhoto(inventoryToDelete.id, "inventory");
        } catch (photoError) {
          console.warn("Gagal menghapus foto inventory:", photoError);
          // Lanjutkan penghapusan meskipun foto gagal dihapus
        }
      }

      // Delete from API
      await deleteInventoryDetail(inventoryToDelete.id);

      // Update state by removing the deleted item
      setEditedItems((prev) => [
        ...prev.slice(0, index),
        ...prev.slice(index + 1),
      ]);

      // Also update originalItems if needed
      setOriginalItems((prev) => [
        ...prev.slice(0, index),
        ...prev.slice(index + 1),
      ]);

      // Reset selection if the deleted item was selected
      if (selectedTableRow === index) {
        setSelectedTableRow(null);
        setSelectedItemEdit(null);
      } else if (selectedTableRow !== null && selectedTableRow > index) {
        // Adjust selected index if it was after the deleted item
        setSelectedTableRow(selectedTableRow - 1);
      }

      // Success notification
      await Swal.fire({
        title: "Berhasil!",
        text: "Data inventory telah dihapus",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Gagal menghapus inventory:", error);
      await Swal.fire({
        title: "Gagal!",
        text: error.message || "Terjadi kesalahan saat menghapus data",
        icon: "error",
      });
    }
  };

  const handleInputChangeEdit = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: any } }
  ) => {
    const { name, value } = event.target;
    setInventoryDataEdit((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNumberInputChangeEdit = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setInventoryDataEdit((prevData) => ({
      ...prevData,
      [name]: Number(value),
    }));
  };

  const handleDateChangeEdit = (name: string, value: Date | null) => {
    setInventoryDataEdit((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditFileUpload = (file: File) => {
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      Swal.fire({
        icon: "warning",
        title: "Unsupported format",
        text: "Only PDF, Excel, Word, and image files are allowed",
      });
      return;
    }

    if (file.size > maxSize) {
      Swal.fire({
        icon: "warning",
        title: "File too large",
        text: "Maximum size is 5MB",
      });
      return;
    }

    // Clean up previous URL if exists
    if (editPhotoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(editPhotoPreview);
    }

    const newPreviewUrl = URL.createObjectURL(file);
    setEditPhotoFile(file);
    setEditPhotoPreview(newPreviewUrl);

    // Update the file in editedItems
    if (selectedTableRow !== null) {
      setEditedItems((prev) =>
        prev.map((item) =>
          item.id === selectedTableRow.id
            ? {
              ...item,
              photo: newPreviewUrl,
              photoFile: file,
              documentUrl: newPreviewUrl,
              documentName: file.name,
            }
            : item
        )
      );
    }
  };

  const editPhotoInputRef = useRef<HTMLInputElement>(null);

  const handleCloseEdit = () => {
    // Reset selected status for all items
    setAvailableItemDetails((prev) =>
      prev.map((item) => ({ ...item, isSelected: false }))
    );

    setOpenEditInventoryDialog(false);
    setIsEditing(false);
    setSelectedInventoryId(null);
  };

  const [errorsEdit, setErrorsEdit] = useState({
    officeId: false,
    totalItems: false,
    itemName: false,
    numbersOfItems: false,
    locationDetail: false,
    conditionStatus: false,
    itemDescription: false,
    area: false,
    building: false,
    floor: false,
    brand: false,
    serialNumber: false,
    assessmentDate: false,
    installationDate: false,
    warrantyExpiry: false,
  });

  const validateFormEdit = () => {
    const newErrors = {
      officeId: !inventoryDataEdit.officeId,
      totalItems: !inventoryDataEdit.totalItems,
      itemDetailId: !inventoryDataEdit.itemDetailId,
      itemName: !inventoryDataEdit.itemName,
      numbersOfItems: !inventoryDataEdit.numbersOfItems,
      locationDetail: !inventoryDataEdit.locationDetail,
      conditionStatus: !inventoryDataEdit.conditionStatus,
      itemDescription: !inventoryDataEdit.itemDescription,
      area: !inventoryDataEdit.area,
      building: !inventoryDataEdit.building,
      floor: !inventoryDataEdit.floor,
      brand: !inventoryDataEdit.brand,
      serialNumber: !inventoryDataEdit.serialNumber,
      assessmentDate: !inventoryDataEdit.assessmentDate,
      installationDate: !inventoryDataEdit.installationDate,
      warrantyExpiry: !inventoryDataEdit.warrantyExpiry,
    };
    setErrorsEdit(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  // Reset form untuk edit
  const resetInventoryFormEdit = () => {
    setInventoryDataEdit({
      id: "",
      inventoryId: "",
      officeId: "",
      officeName: "",
      totalItems: 0,
      itemDetailId: "",
      itemName: "",
      numbersOfItems: 0,
      locationDetail: "",
      conditionStatus: "",
      itemDescription: "",
      area: "",
      building: "",
      floor: "",
      brand: "",
      serialNumber: "",
      assessmentDate: null,
      installationDate: null,
      warrantyExpiry: null,
    });
    setPhotoPreview("");
    setSelectedItemId("");
  };

  // Reset form untuk add
  const resetInventoryFormAdd = () => {
    setInventoryData({
      id: "",
      itemDetailId: "",
      itemName: "",
      numbersOfItems: 0,
      locationDetail: "",
      conditionStatus: "",
      itemDescription: "",
      area: "",
      building: "",
      floor: "",
      brand: "",
      serialNumber: "",
      assessmentDate: null,
      installationDate: null,
      warrantyExpiry: null,
      photo: "",
    });
    setPhotoPreview("");
    setSelectedItemId("");
  };

  const handleAddInventoryDetail = () => {
    // Validasi data wajib
    if (
      !inventoryDataEdit.itemDetailId ||
      !inventoryDataEdit.numbersOfItems ||
      !inventoryDataEdit.locationDetail ||
      !inventoryDataEdit.conditionStatus ||
      !inventoryDataEdit.itemDescription ||
      !inventoryDataEdit.area ||
      !inventoryDataEdit.building ||
      !inventoryDataEdit.floor ||
      !inventoryDataEdit.brand ||
      !inventoryDataEdit.serialNumber ||
      !inventoryDataEdit.assessmentDate ||
      !inventoryDataEdit.installationDate ||
      !inventoryDataEdit.warrantyExpiry
    ) {
      Swal.fire("Error", "Please fill all mandatory fields", "error");
      return;
    }

    // Buat objek item baru
    const newItem: InventoryItem = {
      id: "", // ID kosong untuk item baru
      itemDetailId: inventoryDataEdit.itemDetailId,
      itemName: inventoryDataEdit.itemName,
      numbersOfItems: inventoryDataEdit.numbersOfItems,
      locationDetail: inventoryDataEdit.locationDetail,
      conditionStatus: inventoryDataEdit.conditionStatus,
      itemDescription: inventoryDataEdit.itemDescription,
      area: inventoryDataEdit.area,
      building: inventoryDataEdit.building,
      floor: inventoryDataEdit.floor,
      brand: inventoryDataEdit.brand,
      serialNumber: inventoryDataEdit.serialNumber,
      assessmentDate: inventoryDataEdit.assessmentDate,
      installationDate: inventoryDataEdit.installationDate,
      warrantyExpiry: inventoryDataEdit.warrantyExpiry,
      photo: editPhotoPreview,
      photoFile: editPhotoFile,
    };

    // Tambahkan ke tabel
    setEditedItems([...editedItems, newItem]);

    // Reset form
    setInventoryDataEdit({
      ...inventoryDataEdit,
      itemDetailId: "",
      itemName: "",
      numbersOfItems: 0,
      locationDetail: "",
      conditionStatus: "",
      itemDescription: "",
      area: "",
      building: "",
      floor: "",
      brand: "",
      serialNumber: "",
      assessmentDate: null,
      installationDate: null,
      warrantyExpiry: null,
    });
    setEditPhotoPreview("");
    setEditPhotoFile(null);
  };

  const handleEditInventoryDetail = () => {
    if (!selectedTableRow) {
      Swal.fire("Error", "No item selected", "error");
      return;
    }
    console.log("selected", selectedTableRow);

    // Validasi data wajib
    const requiredFields = {
      itemDetailId: inventoryDataEdit.itemDetailId,
      numbersOfItems: inventoryDataEdit.numbersOfItems,
      locationDetail: inventoryDataEdit.locationDetail,
      conditionStatus: inventoryDataEdit.conditionStatus,
      itemDescription: inventoryDataEdit.itemDescription,
      area: inventoryDataEdit.area,
      building: inventoryDataEdit.building,
      floor: inventoryDataEdit.floor,
      brand: inventoryDataEdit.brand,
      serialNumber: inventoryDataEdit.serialNumber,
      assessmentDate: inventoryDataEdit.assessmentDate,
      installationDate: inventoryDataEdit.installationDate,
      warrantyExpiry: inventoryDataEdit.warrantyExpiry,
    };

    if (Object.values(requiredFields).some((field) => !field)) {
      Swal.fire("Error", "Please fill all mandatory fields", "error");
      return;
    }

    // Buat item yang diupdate
    const updatedItem: InventoryItem = {
      id: selectedTableRow.id,
      itemDetailId: inventoryDataEdit.itemDetailId!,
      itemName: inventoryDataEdit.itemName || selectedTableRow.itemName,
      numbersOfItems: inventoryDataEdit.numbersOfItems!,
      locationDetail: inventoryDataEdit.locationDetail!,
      conditionStatus: inventoryDataEdit.conditionStatus!,
      itemDescription: inventoryDataEdit.itemDescription!,
      area: inventoryDataEdit.area,
      building: inventoryDataEdit.building,
      floor: inventoryDataEdit.floor!,
      brand: inventoryDataEdit.brand!,
      serialNumber: inventoryDataEdit.serialNumber!,
      assessmentDate: inventoryDataEdit.assessmentDate!,
      installationDate: inventoryDataEdit.installationDate!,
      warrantyExpiry: inventoryDataEdit.warrantyExpiry!,
      photo: editPhotoPreview || selectedTableRow.photo || "",
      photoFile: editPhotoFile || selectedTableRow.photoFile || null,
    };

    // Update data di tabel
    setEditedItems((prevItems) =>
      prevItems.map((item) =>
        item.id === selectedTableRow.id ? updatedItem : item
      )
    );

    // Reset form
    setEditPhotoPreview("");
    setEditPhotoFile(null);
    setSelectedTableRow(null);
    setIsEditingExistingItem(false);
    resetForm();
  };

  const createDetailPayload = (
    item: InventoryItem,
    inventoryId: string
  ): InventoryDetailCreatePayload => {
    if (
      !item.locationDetail ||
      !item.conditionStatus ||
      !item.itemDescription
    ) {
      throw new Error(
        "Semua field wajib diisi: location, condition, description"
      );
    }

    return {
      inventoryId,
      itemDetailId: item.itemDetailId,
      numberOfItems: item.numbersOfItems,
      locationInfo: item.locationDetail,
      assetCondition: item.conditionStatus,
      detailDescription: item.itemDescription,
      area: item.area,
      building: item.building,
      floor: item.floor,
      brand: item.brand,
      serialNumber: item.serialNumber,
      assessmentDate: item.assessmentDate,
      installationDate: item.installationDate,
      warrantyExpiry: item.warrantyExpiry,
    };
  };

  const handleSaveEdit = async () => {
    try {
      setIsLoading(true);

      // 1. Validasi data
      if (!inventoryDataEdit.id) {
        throw new Error("Inventory ID tidak valid");
      }

      // 2. Update Inventory Header
      await updateInventory({
        id: inventoryDataEdit.inventoryId, // Use the inventory ID
        officeId: inventoryDataEdit.officeId,
        totalItems: inventoryDataEdit.totalItems,
        itemName: " ",
        quantity: 0,
        conditionStatus: " ",
      });

      // 3. Process Inventory Details
      for (const item of editedItems) {
        try {
          // Validate required fields
          if (
            !item.itemDetailId ||
            !item.numbersOfItems ||
            !item.locationDetail ||
            !item.conditionStatus ||
            !item.itemDescription
          ) {
            console.warn("Invalid item:", item);
            continue;
          }

          const detailPayload = {
            inventoryId: inventoryDataEdit.inventoryId, // Use inventory ID
            itemDetailId: item.itemDetailId,
            numberOfItems: item.numbersOfItems,
            locationInfo: item.locationDetail,
            assetCondition: item.conditionStatus,
            detailDescription: item.itemDescription,
            area: item.area,
            building: item.building,
            floor: item.floor,
            brand: item.brand,
            serialNumber: item.serialNumber,
            assessmentDate: item.assessmentDate,
            installationDate: item.installationDate,
            warrantyExpiry: item.warrantyExpiry,
          };
          console.log("tes", detailPayload);

          if (!item.id) {
            // Create new detail
            const response = await createInventoryDetail(detailPayload);
            item.id = response.id;

            // Upload photo if exists
            if (item.photoFile) {
              await uploadItemPhoto(response.id, item.photoFile);
            }
          } else {
            // Update existing detail
            await updateInventoryDetail({
              ...detailPayload,
              id: item.id, // Inventory detail ID
            });

            // Handle photo update
            if (item.photoFile) {
              // Delete old photo if exists
              if (item.photo) {
                try {
                  await deleteItemPhoto(item.id, "inventory");
                } catch (error) {
                  console.warn("Failed to delete old photo:", error);
                }
              }
              // Upload new photo
              await uploadItemPhoto(item.id, item.photoFile);
            }
          }
        } catch (error) {
          console.error("Failed to process item:", item, error);
        }
      }

      // 4. Delete removed items
      const currentItemIds = editedItems.map((i) => i.id).filter(Boolean);
      for (const originalItem of originalItems) {
        if (!currentItemIds.includes(originalItem.id)) {
          try {
            if (originalItem.photo) {
              await deleteItemPhoto(originalItem.id, "inventory");
            }
            await deleteInventoryDetail(originalItem.id);
          } catch (error) {
            console.error("Failed to delete item:", originalItem.id, error);
          }
        }
      }

      // Success notification
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Inventory saved successfully",
        confirmButtonColor: "#0F62FE",
      });

      handleCloseEdit();
      reloadInventory();
    } catch (error) {
      console.error("Failed to save inventory:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message || "Save failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fungsi pembantu untuk hapus item
  const deleteRemovedItems = async () => {
    const currentIds = editedItems.map((i) => i.id).filter(Boolean);
    const itemsToDelete = originalItems.filter(
      (item) => !currentIds.includes(item.id)
    );

    for (const item of itemsToDelete) {
      try {
        if (item.photo) await deleteItemPhoto(item.id, "inventory");
        await deleteInventoryDetail(item.id);
      } catch (error) {
        console.error(`Gagal menghapus item ${item.id}:`, error);
      }
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup object URLs
      editedItems.forEach((item) => {
        if (item.photo && item.photo.startsWith("blob:")) {
          URL.revokeObjectURL(item.photo);
        }
      });
      if (editPhotoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(editPhotoPreview);
      }
    };
  }, [editedItems, editPhotoPreview]);

  useEffect(() => {
    if (openEditInventoryDialog && inventoryDataEdit.itemDetailId) {
      const matchedItem = itemDetails.find(
        (item) => item.id === inventoryDataEdit.itemDetailId
      );
      setSelectedItemEdit(matchedItem || null);
    }
  }, [itemDetails, openEditInventoryDialog, inventoryDataEdit.itemDetailId]);

  return isLoading ? (
    <LoadingOverlay />
  ) : (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 2 }}
      >
        <Typography variant="h6" fontWeight="bold">
          Inventory List
        </Typography>
      </Box>
      {/* Toolbar */}
      <Box className="toolbar">
        {/* Left Controls */}
        <Box className="leftControls">
          <Typography>Show</Typography>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            size="small"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>

          {/* Search Button & Input */}
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => setShowSearch((prev) => !prev)}
            className="searchButton"
          >
            Search
          </Button>
          {showSearch && (
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              value={searchQuery}
              onChange={async (e) => {
                await setSearchQuery(e.target.value);
              }}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              autoFocus
              className="searchInput"
            />
          )}

          {/* Error Display */}
          {error && <Typography color="error">{error}</Typography>}
        </Box>

        {/* Right Controls */}
        <Box className="rightControls">
          <Button
            startIcon={<Delete />}
            variant="text"
            onClick={handleDeleteInventory}
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>

          {/* Filter Button */}
          <Button
            startIcon={<FilterList />}
            variant="text"
            onClick={() => setOpenFilterDialog(true)}
          >
            Filters
          </Button>

          <Button
            startIcon={<Download />}
            variant="contained"
            className="exportButton"
            onClick={handleExport}
          >
            Export
          </Button>

          <Button
            startIcon={<Add />}
            variant="contained"
            className="addCategoryButton"
            onClick={handleOpenAddDialog}
          >
            Add New Inventory
          </Button>
        </Box>
      </Box>
      <Box className="deleteContainer">
        <Typography variant="body2" className="detailsSubtitle">
          Details List Of Inventory
        </Typography>
      </Box>
      {/* Table */}
      <TableContainer component={Paper} className="tableContainer">
        <Table className="table">
          <TableHead className="tableHead">
            <TableRow>
              <TableCell padding="checkbox" className="tableCell">
                <Checkbox
                  indeterminate={
                    selectedRows.length > 0 && selectedRows.length < rows.length
                  }
                  checked={selectedRows.length === rows.length}
                  onChange={() =>
                    setSelectedRows(
                      selectedRows.length === rows.length
                        ? []
                        : rows.map((row) => row.id)
                    )
                  }
                />
              </TableCell>
              <TableCell className="tableCell" width="50px">
                <strong>No</strong>
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("office.officeName")}
                style={{ cursor: "pointer" }}
              >
                <strong>Office</strong>
                {sortConfig.orderBy === "office.officeName" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell
                className="tableCell"
                onClick={() => handleSort("totalItems")}
                style={{ cursor: "pointer" }}
              >
                <strong>Total Items</strong>
                {sortConfig.orderBy === "totalItems" &&
                  (sortConfig.order === "asc" ? " ▲" : " ▼")}
              </TableCell>
              <TableCell className="tableCell"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={12} align="center" style={{ color: "red" }}>
                  {error}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  No inventory records found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.id} selected={selectedRows.includes(row.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRows.includes(row.id)}
                      onChange={(event) => {
                        event.stopPropagation();
                        handleSelectRow(row.id);
                      }}
                    />
                  </TableCell>
                  <TableCell>{offset + index + 1}</TableCell>
                  <TableCell>{row.officeName || "No office data"}</TableCell>
                  <TableCell>{row.totalItems}</TableCell>
                  <TableCell className="tableCell">
                    <IconButton onClick={() => handleOpenEditDialog(row)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination */}
      <Box className="paginationContainer">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {`Showing ${offset + 1} to ${Math.min(
            offset + rowsPerPage,
            totalItems
          )} of ${totalItems} entries`}
        </Typography>

        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, page) => setCurrentPage(page)}
          className="pagination"
          showFirstButton
          showLastButton
          renderItem={(item) => (
            <PaginationItem
              {...item}
              components={{ first: FirstPage, last: LastPage }}
            />
          )}
        />
      </Box>
      {/* Filter Dialog */}
      <Dialog
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
      >
        <DialogTitle>Advanced Search</DialogTitle>
        <DialogContent>
          {filters.map((filter, index) => {
            const usedFields = filters
              .map((f, i) => i !== index && f.field)
              .filter(Boolean);
            const availableOptions = availableFilters.filter(
              (opt) => !usedFields.includes(opt.value)
            );

            const conditionOptions = ["New", "Good", "Poor", "Maintenance"];

            return (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                gap={1}
                mb={2}
              >
                <TextField
                  select
                  value={filter.field}
                  onChange={(e) =>
                    handleFilterChange(index, "field", e.target.value)
                  }
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, minWidth: 160 }}
                >
                  {availableOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {filter.field === "conditionStatus" ? (
                  <TextField
                    select
                    label="Condition"
                    variant="outlined"
                    size="small"
                    value={filter.value}
                    onChange={(e) =>
                      handleFilterChange(index, "value", e.target.value)
                    }
                    sx={{ mt: 1, minWidth: 160 }}
                  >
                    {conditionOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : filter.field === "officeId" ? (
                  <Autocomplete
                    options={officeOptions}
                    getOptionLabel={(option) => option.officeName}
                    value={
                      officeOptions.find((o) => o.officeId === filter.value) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      handleFilterChange(
                        index,
                        "value",
                        newValue?.officeName || ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Office"
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1, minWidth: 160 }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label="Value"
                    variant="outlined"
                    size="small"
                    value={filter.value}
                    onChange={(e) =>
                      handleFilterChange(index, "value", e.target.value)
                    }
                    sx={{ mt: 1 }}
                  />
                )}

                {index > 0 && (
                  <IconButton onClick={() => handleRemoveFilter(index)}>
                    <Remove />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {filters.length < availableFilters.length && (
            <Button startIcon={<Add />} onClick={handleAddFilter}>
              Add Filter
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetFilters}>Reset Filter</Button>
          <Button onClick={handleCloseFilter}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ===================Add Inventory Dialog==================== */}
      <Dialog
        open={openAddInventoryDialog}
        onClose={handleCloseAdd}
        fullWidth
        maxWidth="lg"
        sx={{ backdropFilter: "blur(5px)" }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Add New Inventory
          </Typography>
          <IconButton onClick={handleCloseAdd} color="error">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6}>
                <Autocomplete
                  options={officeOptions}
                  getOptionLabel={(option) => option.officeName}
                  value={selectedOffice}
                  onChange={(event, newValue) => {
                    setSelectedOffice(newValue);
                    setInventoryDataAdd((prev) => ({
                      ...prev,
                      officeId: newValue ? newValue.officeId : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Office"
                      margin="normal"
                      variant="outlined"
                      error={!inventoryDataAdd.officeId}
                      helperText={
                        !inventoryDataAdd.officeId ? "Mandatory Field" : ""
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <TextField
                  fullWidth
                  label="Total Items"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={inventoryDataAdd.totalItems}
                  sx={{ mb: 1 }}
                  name="totalItems"
                  error={!inventoryDataAdd.totalItems}
                  helperText={
                    !inventoryDataAdd.totalItems ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeAdd}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              mb: 4,
              p: 2,
              border: "1px solid #ddd",
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Inventory Items
            </Typography>

            {inventoryItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  mb: 4,
                  p: 2,
                  border: "1px solid #eee",
                  borderRadius: 1,
                  position: "relative",
                }}
              >
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Item #{index + 1}
                </Typography>

                {index > 0 && (
                  <IconButton
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    onClick={() =>
                      setInventoryItems((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={6} sm={6}>
                    <Autocomplete
                      options={getAvailableItems()}
                      getOptionLabel={(option) =>
                        typeof option === "string"
                          ? option
                          : option?.itemDetailName || option?.itemName || " "
                      }
                      value={
                        availableItemDetails.find(
                          (d) => d.id === item.itemDetailId
                        ) || {
                          id: item.itemDetailId,
                          itemDetailName: item.itemName,
                        }
                      }
                      onChange={(e, newValue) => {
                        handleItemChangeAdd(
                          index,
                          "itemDetailId",
                          newValue?.id || ""
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Item Name"
                          error={!item.itemDetailId}
                          helperText={!item.itemDetailId ? "Required" : ""}
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                    />

                    <TextField
                      fullWidth
                      label="Numbers Of Items"
                      margin="normal"
                      variant="outlined"
                      type="number"
                      value={item.numbersOfItems}
                      sx={{ mb: 0 }}
                      onChange={(e) =>
                        handleItemChangeAdd(
                          index,
                          "numbersOfItems",
                          Number(e.target.value)
                        )
                      }
                      error={!item.numbersOfItems}
                      helperText={!item.numbersOfItems ? "Mandatory Field" : ""}
                    />

                    <TextField
                      select
                      fullWidth
                      label="Condition Status"
                      margin="normal"
                      variant="outlined"
                      value={item.conditionStatus}
                      sx={{ mb: 0 }}
                      onChange={(e) =>
                        handleItemChangeAdd(
                          index,
                          "conditionStatus",
                          e.target.value
                        )
                      }
                      error={!item.conditionStatus}
                      helperText={
                        !item.conditionStatus ? "Mandatory Field" : ""
                      }
                    >
                      <MenuItem value="New">New</MenuItem>
                      <MenuItem value="Good">Good</MenuItem>
                      <MenuItem value="Poor">Poor</MenuItem>
                      <MenuItem value="Maintenance">Maintenance</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      label="Area"
                      margin="normal"
                      variant="outlined"
                      value={item.area}
                      sx={{ mb: 0 }}
                      onChange={(e) =>
                        handleItemChangeAdd(index, "area", e.target.value)
                      }
                      error={!item.area}
                      helperText={!item.area ? "Mandatory Field" : ""}
                    />

                    <TextField
                      fullWidth
                      label="Building"
                      margin="normal"
                      variant="outlined"
                      value={item.building}
                      sx={{ mb: 0 }}
                      onChange={(e) =>
                        handleItemChangeAdd(index, "building", e.target.value)
                      }
                      error={!item.building}
                      helperText={!item.building ? "Mandatory Field" : ""}
                    />

                    <TextField
                      fullWidth
                      label="Floor"
                      margin="normal"
                      variant="outlined"
                      value={item.floor}
                      sx={{ mb: 0 }}
                      onChange={(e) =>
                        handleItemChangeAdd(index, "floor", e.target.value)
                      }
                      error={!item.floor}
                      helperText={!item.floor ? "Mandatory Field" : ""}
                    />

                    <TextField
                      fullWidth
                      label="Brand"
                      margin="normal"
                      variant="outlined"
                      value={item.brand}
                      sx={{ mb: 1 }}
                      onChange={(e) =>
                        handleItemChangeAdd(index, "brand", e.target.value)
                      }
                      error={!item.brand}
                      helperText={!item.brand ? "Mandatory Field" : ""}
                    />

                    {/* Photo Upload */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #aaa",
                        padding: 4,
                        borderRadius: "12px",
                        backgroundColor: "#fafafa",
                        textAlign: "center",
                        cursor: "pointer",
                        position: "relative",
                        mb: 2,
                      }}
                      onClick={() =>
                        document.getElementById(`file-upload-${index}`)?.click()
                      }
                    >
                      {item.photo ? (
                        <>
                          {item.photo.type?.startsWith("image/") ? (
                            <img
                              src={item.photo}
                              alt="File Preview"
                              style={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "8px",
                                objectFit: "contain",
                                maxHeight: 180,
                              }}
                            />
                          ) : (
                            <>
                              <GridOn fontSize="large" color="primary" />
                              <Typography variant="body2" mt={1}>
                                {item.photoFile?.name || "Uploaded File"}
                              </Typography>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <GridOn fontSize="large" />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            mt={1}
                          >
                            Drag & drop or click to upload file
                          </Typography>
                        </>
                      )}
                      <input
                        hidden
                        id={`file-upload-${index}`}
                        accept=".pdf,.doc,.docx,.xls,.xlsx"
                        type="file"
                        onChange={(e) =>
                          e.target.files?.[0] &&
                          handleItemFileUploadAdd(index, e.target.files[0])
                        }
                      />
                      <Typography variant="caption" mt={1}>
                        Max size: 5MB (PDF, CSV/XLS/XLSX, WORD)
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={6}>
                    <TextField
                      fullWidth
                      label="Location Detail"
                      multiline
                      rows={3}
                      margin="normal"
                      variant="outlined"
                      value={item.locationDetail}
                      sx={{ mt: 0, mb: 1 }}
                      onChange={(e) =>
                        handleItemChangeAdd(
                          index,
                          "locationDetail",
                          e.target.value
                        )
                      }
                      error={!item.locationDetail}
                      helperText={!item.locationDetail ? "Mandatory Field" : ""}
                    />

                    <TextField
                      fullWidth
                      label="Item Description"
                      multiline
                      rows={3}
                      margin="normal"
                      variant="outlined"
                      value={item.itemDescription}
                      sx={{ mb: 1 }}
                      onChange={(e) =>
                        handleItemChangeAdd(
                          index,
                          "itemDescription",
                          e.target.value
                        )
                      }
                      error={!item.itemDescription}
                      helperText={
                        !item.itemDescription ? "Mandatory Field" : ""
                      }
                    />

                    <TextField
                      fullWidth
                      label="Serial Number"
                      margin="normal"
                      variant="outlined"
                      value={item.serialNumber}
                      sx={{ mb: 1 }}
                      onChange={(e) =>
                        handleItemChangeAdd(
                          index,
                          "serialNumber",
                          e.target.value
                        )
                      }
                      error={!item.serialNumber}
                      helperText={!item.serialNumber ? "Mandatory Field" : ""}
                    />

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Assessment Date"
                        format="YYYY-MM-DD"
                        value={
                          item.assessmentDate
                            ? dayjs(item.assessmentDate)
                            : null
                        }
                        onChange={(newValue) =>
                          handleItemChangeAdd(
                            index,
                            "assessmentDate",
                            newValue?.toDate() || null
                          )
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { mb: 1 },
                            error: !item.assessmentDate,
                            helperText: !item.assessmentDate
                              ? "Mandatory Field"
                              : "",
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Installation Date"
                        format="YYYY-MM-DD"
                        value={
                          item.warrantyExpiry
                            ? dayjs(item.installationDate)
                            : null
                        }
                        onChange={(newValue) =>
                          handleItemChangeAdd(
                            index,
                            "installationDate",
                            newValue?.toDate() || null
                          )
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { mb: 1 },
                            error: !item.installationDate,
                            helperText: !item.installationDate
                              ? "Mandatory Field"
                              : "",
                          },
                        }}
                      />
                    </LocalizationProvider>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Warranty/Expired Date"
                        format="YYYY-MM-DD"
                        value={
                          item.warrantyExpiry
                            ? dayjs(item.warrantyExpiry)
                            : null
                        }
                        onChange={(newValue) =>
                          handleItemChangeAdd(
                            index,
                            "warrantyExpiry",
                            newValue?.toDate() || null
                          )
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: { mb: 1 },
                            error: !item.warrantyExpiry,
                            helperText: !item.warrantyExpiry
                              ? "Mandatory Field"
                              : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button
              startIcon={<Add />}
              onClick={newItemAdd}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Another Item
            </Button>
          </Box>
        </DialogContent>

        {/* Buttons: Save & Close */}
        <DialogActions
          sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
        >
          <Button variant="outlined" color="error" onClick={handleCloseAdd}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleSaveAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* ================================Edit Inventory Dialog================================ */}
      <Dialog
        open={openEditInventoryDialog}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="lg"
        sx={{ backdropFilter: "blur(5px)" }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {isEditing ? "Edit Inventory" : "Inventory Details"}
          </Typography>
          <IconButton onClick={handleCloseEdit} color="error">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6}>
                <Autocomplete
                  options={officeOptions}
                  getOptionLabel={(option) => option.officeName}
                  value={
                    officeOptions.find(
                      (office) => office.officeId === inventoryDataEdit.officeId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setInventoryDataEdit((prev) => ({
                      ...prev,
                      officeId: newValue ? newValue.officeId : "",
                      officeName: newValue ? newValue.officeName : "",
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Office"
                      error={!inventoryDataEdit.officeId}
                      helperText={!inventoryDataEdit.officeId ? "Required" : ""}
                      disabled={!isEditing}
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <TextField
                  fullWidth
                  label="Total Items"
                  margin="normal"
                  variant="outlined"
                  type="number"
                  value={inventoryDataEdit.totalItems}
                  sx={{ mb: 1 }}
                  name="totalItems"
                  disabled={!isEditing}
                  error={isEditing && errorsEdit.totalItems}
                  helperText={
                    isEditing && errorsEdit.totalItems ? "Mandatory Field" : ""
                  }
                  onChange={handleNumberInputChangeEdit}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              mb: 4,
              p: 2,
              border: "1px solid #ddd",
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Inventory Details
            </Typography>
            <Box
              sx={{
                mb: 4,
                p: 2,
                border: "1px solid #ddd",
                borderRadius: 1,
                position: "relative",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} sm={6}>
                  <Autocomplete
                    options={availableItemDetails}
                    getOptionLabel={(option) => option.itemDetailName || " "}
                    value={
                      availableItemDetails.find(
                        (item) => item.id === inventoryDataEdit.itemDetailId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setInventoryDataEdit((prev) => ({
                          ...prev,
                          itemDetailId: newValue.id,
                          itemName: newValue.itemDetailName,
                        }));

                        // Update selected status
                        setAvailableItemDetails((prev) =>
                          prev.map((item) => ({
                            ...item,
                            isSelected:
                              item.id === newValue.id ||
                              (item.isSelected &&
                                item.id !== inventoryDataEdit.itemDetailId),
                          }))
                        );
                      }
                    }}
                    disabled={!isEditing}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item Name"
                        variant="outlined"
                        sx={{ mt: 2 }}
                        error={isEditing && !inventoryDataEdit.itemDetailId}
                        helperText={
                          isEditing && !inventoryDataEdit.itemDetailId
                            ? "Mandatory Field"
                            : ""
                        }
                      />
                    )}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        option.itemDetailName
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Numbers Of Items"
                    margin="normal"
                    variant="outlined"
                    type="number"
                    value={inventoryDataEdit.numbersOfItems}
                    sx={{ mb: 0 }}
                    name="numbersOfItems"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.numbersOfItems}
                    helperText={
                      isEditing && errorsEdit.numbersOfItems
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleNumberInputChangeEdit}
                  />
                  <TextField
                    select
                    fullWidth
                    label="Condition Status"
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.conditionStatus}
                    sx={{ mb: 0 }}
                    name="conditionStatus"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.conditionStatus}
                    helperText={
                      isEditing && errorsEdit.conditionStatus
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleInputChangeEdit}
                  >
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="Good">Good</MenuItem>
                    <MenuItem value="Poor">Poor</MenuItem>
                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    label="Area"
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.area}
                    sx={{ mb: 0 }}
                    name="area"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.area}
                    helperText={
                      isEditing && errorsEdit.area ? "Mandatory Field" : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  <TextField
                    fullWidth
                    label="Building"
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.building}
                    sx={{ mb: 0 }}
                    name="building"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.building}
                    helperText={
                      isEditing && errorsEdit.building ? "Mandatory Field" : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  <TextField
                    fullWidth
                    label="Floor"
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.floor}
                    sx={{ mb: 0 }}
                    name="floor"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.floor}
                    helperText={
                      isEditing && errorsEdit.floor ? "Mandatory Field" : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  <TextField
                    fullWidth
                    label="Brand"
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.brand}
                    sx={{ mb: 1 }}
                    name="brand"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.brand}
                    helperText={
                      isEditing && errorsEdit.brand ? "Mandatory Field" : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  {/* Photo Upload */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #aaa",
                      padding: 4,
                      borderRadius: "12px",
                      backgroundColor: "#fafafa",
                      textAlign: "center",
                      cursor: isEditing ? "pointer" : "default",
                      position: "relative",
                      mb: 2,
                    }}
                    onClick={() =>
                      isEditing && editPhotoInputRef.current?.click()
                    }
                  >
                    {editPhotoPreview ? (
                      <>
                        {/* Tampilkan preview berdasarkan tipe file */}
                        {editPhotoFile?.type.startsWith("image/") ? (
                          <img
                            src={editPhotoPreview}
                            alt="File Preview"
                            style={{
                              width: "100%",
                              height: "auto",
                              maxHeight: 180,
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                          />
                        ) : (
                          <Box sx={{ textAlign: "center" }}>
                            <PictureAsPdfIcon
                              fontSize="large"
                              color="primary"
                            />
                            <Typography variant="body2" mt={1}>
                              {editPhotoFile?.name || "Uploaded File"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Click to preview/download
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        <CloudUpload fontSize="large" color="primary" />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mt={1}
                        >
                          {isEditing
                            ? "Drag & drop or click to upload file (PDF, Excel, Word, Images)"
                            : "No file uploaded"}
                        </Typography>
                      </>
                    )}
                    <input
                      hidden
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      type="file"
                      ref={editPhotoInputRef}
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleEditFileUpload(e.target.files[0]);
                        }
                      }}
                      disabled={!isEditing}
                    />
                    <Typography variant="caption" mt={1}>
                      Max size: 5MB (PDF, Excel, Word, Images)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    fullWidth
                    label="Location Detail"
                    multiline
                    rows={3}
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.locationDetail}
                    sx={{ mb: 1 }}
                    name="locationDetail"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.locationDetail}
                    helperText={
                      isEditing && errorsEdit.locationDetail
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  <TextField
                    fullWidth
                    label="Item Description"
                    multiline
                    rows={3}
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.itemDescription}
                    sx={{ mb: 1 }}
                    name="itemDescription"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.itemDescription}
                    helperText={
                      isEditing && errorsEdit.itemDescription
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  <TextField
                    fullWidth
                    label="Serial Number"
                    margin="normal"
                    variant="outlined"
                    value={inventoryDataEdit.serialNumber}
                    sx={{ mb: 1 }}
                    name="serialNumber"
                    disabled={!isEditing}
                    error={isEditing && errorsEdit.serialNumber}
                    helperText={
                      isEditing && errorsEdit.serialNumber
                        ? "Mandatory Field"
                        : ""
                    }
                    onChange={handleInputChangeEdit}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Assessment Date"
                      format="YYYY-MM-DD"
                      value={
                        inventoryDataEdit.assessmentDate
                          ? dayjs(inventoryDataEdit.assessmentDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeEdit(
                          "assessmentDate",
                          newValue?.toDate() || null
                        )
                      }
                      disabled={!isEditing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          error: isEditing && errorsEdit.assessmentDate,
                          helperText:
                            isEditing && errorsEdit.assessmentDate
                              ? "Mandatory Field"
                              : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Installation Date"
                      format="YYYY-MM-DD"
                      value={
                        inventoryDataEdit.installationDate
                          ? dayjs(inventoryDataEdit.installationDate)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeEdit(
                          "installationDate",
                          newValue?.toDate() || null
                        )
                      }
                      disabled={!isEditing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          error: isEditing && errorsEdit.installationDate,
                          helperText:
                            isEditing && errorsEdit.installationDate
                              ? "Mandatory Field"
                              : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Warranty Date"
                      format="YYYY-MM-DD"
                      value={
                        inventoryDataEdit.warrantyExpiry
                          ? dayjs(inventoryDataEdit.warrantyExpiry)
                          : null
                      }
                      onChange={(newValue) =>
                        handleDateChangeEdit(
                          "warrantyExpiry",
                          newValue?.toDate() || null
                        )
                      }
                      disabled={!isEditing}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: { mb: 1 },
                          error: isEditing && errorsEdit.installationDate,
                          helperText:
                            isEditing && errorsEdit.installationDate
                              ? "Mandatory Field"
                              : "",
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Box>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
            >
              {isEditingExistingItem ? (
                <>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={isLoading || !isEditing}
                    sx={{ mr: 2 }}
                  >
                    CANCEL
                  </Button>
                  <Button
                    variant="contained"
                    onClick={
                      isEditingExistingItem
                        ? handleEditInventoryDetail
                        : handleAddInventoryDetail
                    }
                    disabled={
                      !inventoryDataEdit.itemDetailId ||
                      !inventoryDataEdit.numbersOfItems ||
                      !inventoryDataEdit.locationDetail ||
                      !inventoryDataEdit.conditionStatus ||
                      !inventoryDataEdit.itemDescription ||
                      !inventoryDataEdit.area ||
                      !inventoryDataEdit.building ||
                      !inventoryDataEdit.floor ||
                      !inventoryDataEdit.brand ||
                      !inventoryDataEdit.serialNumber ||
                      !inventoryDataEdit.installationDate ||
                      !inventoryDataEdit.assessmentDate ||
                      !inventoryDataEdit.warrantyExpiry ||
                      !isEditing
                    }
                    color="primary"
                  >
                    {isEditingExistingItem
                      ? "EDIT INVENTORY DETAIL"
                      : "TAMBAHKAN KE INVENTORY"}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleAddInventoryDetail}
                  disabled={isLoading || !inventoryDataEdit.itemDetailId}
                  color="primary"
                >
                  {isLoading ? "Processing..." : "TAMBAHKAN KE INVENTORY"}
                </Button>
              )}
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Detail Breakdown for {inventoryDataEdit.officeName}
              </Typography>
              <Box>

                {/* Date Filters */}
                <Box display="flex" gap={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Date From"
                      format="YYYY-MM-DD"
                      value={startDateE ? dayjs(startDateE) : null}
                      onChange={(newValue) =>
                        setStartDateE(newValue ? newValue.format("YYYY-MM-DD") : null)
                      }
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          size: "small",
                          sx: { minWidth: 150 },
                        },
                      }}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                      label="Date To"
                      format="YYYY-MM-DD"
                      value={endDateE ? dayjs(endDateE) : null}
                      onChange={(newValue) =>
                        setEndDateE(newValue ? newValue.format("YYYY-MM-DD") : null)
                      }
                      slotProps={{
                        textField: {
                          variant: "outlined",
                          size: "small",
                          sx: { minWidth: 150 },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              <Table size="small" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>No</TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Warranty Expiry</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>File Document</TableCell>{" "}
                    {/* Ganti dari "File" */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editedItems.filter((row) => {
                    if (!startDateE && !endDateE) return true; // tidak ada filter

                    const warrantyExpiry = dayjs(row.warrantyExpiry);
                    const start = startDateE ? dayjs(startDateE) : null;
                    const end = endDateE ? dayjs(endDateE) : null;

                    if (start && end) {
                      return warrantyExpiry.isSameOrAfter(start) && warrantyExpiry.isSameOrBefore(end);
                    }
                    if (start) {
                      return warrantyExpiry.isSameOrAfter(start);
                    }
                    if (end) {
                      return warrantyExpiry.isSameOrBefore(end);
                    }
                    return true;
                  }).map((row, index) => (
                    <TableRow
                      key={row.itemDetailId || index}
                      onClick={() => handleTableRowClick(row)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedTableRow?.id === row.id
                            ? "#f5f5f5"
                            : "inherit",
                        "&:hover": {
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                    >
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleDeleteInventoryDetailRow(index);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.itemName || "-"}</TableCell>
                      <TableCell>{row.locationDetail || "-"}</TableCell>
                      <TableCell>
                        {row.warrantyExpiry
                          ? new Date(row.warrantyExpiry).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell>{row.numbersOfItems || 0}</TableCell>
                      <TableCell>{row.itemDescription || "-"}</TableCell>
                      <TableCell>{row.conditionStatus || "-"}</TableCell>
                      <TableCell>
                        {row.photo || row.documentUrl ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {/* Tampilkan ikon berdasarkan tipe file */}
                            {row.photo?.includes(".pdf") ||
                              row.documentName?.includes(".pdf") ? (
                              // FILE PDF - TAMPILKAN ICON + FILENAME
                              <>
                                <PictureAsPdfIcon
                                  color="error"
                                  sx={{ cursor: "pointer", fontSize: 24 }}
                                />
                                <Typography variant="caption">
                                  {row.documentName || "Document.pdf"}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadFile(
                                      row.documentName || row.photo || ""
                                    );
                                  }}
                                >
                                  Download
                                </Button>
                              </>
                            ) : row.photo?.startsWith("blob:") ||
                              row.photoFile ? (
                              // FILE YANG BARU DIUPLOAD - TAMPILKAN ICON
                              <>
                                <GridOn
                                  color="primary"
                                  sx={{ cursor: "pointer", fontSize: 24 }}
                                />
                                <Typography variant="caption">
                                  {row.photoFile?.name || "Uploaded File"}
                                </Typography>
                              </>
                            ) : row.photo ? (
                              // URL GAMBAR - TAMPILKAN PREVIEW (jika masih ingin tampilkan gambar)
                              <img
                                src={row.photo}
                                alt="Item"
                                style={{
                                  width: 50,
                                  height: 50,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                }}
                                onError={(e) => {
                                  // Fallback jika bukan gambar
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            ) : (
                              // FALLBACK
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                No file
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            No file
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Box textAlign={"right"} sx={{ mt: 2, mb: 2 }}>
              <Button
                onClick={exportInventoryItemsToPDF}
                variant="outlined"
                startIcon={<PictureAsPdf />}
                color="error"
              >
                Export PDF
              </Button>
              <Button
                onClick={exportInventoryItemsToExcel}
                variant="outlined"
                startIcon={<Download />}
                color="success"
                sx={{ ml: 1 }}
              >
                Export Excel
              </Button>
            </Box>
          </Box>
        </DialogContent>

        {/* Buttons: Save & Close */}
        <DialogActions sx={{ justifyContent: "space-between", padding: 2 }}>
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              variant="contained"
              color="primary"
              sx={{ width: 120 }}
            >
              EDIT
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setIsEditing(false);
                }}
                variant="outlined"
                color="error"
                sx={{ width: 120 }}
              >
                CANCEL
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="contained"
                color="success"
                sx={{ width: 120 }}
                disabled={
                  JSON.stringify(editedItems) === JSON.stringify(originalItems)
                }
              >
                SAVE
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryListPage;
